import {
  type QueryKey,
  type UseQueryOptions,
  useQuery,
} from "@tanstack/react-query";

// Error handling
export interface APIErrorData {
  error?: string;
  error_code?: string;
  message?: string;
  [key: string]: unknown;
}

/**
 * Partial error for non-fatal issues during request processing.
 * Used when a request partially succeeds (e.g., data returned but metrics timed out).
 */
export interface PartialError {
  code: string;
  message: string;
}

export class APIError extends Error {
  constructor(
    message: string,
    public status?: number,
    public data?: APIErrorData | null,
  ) {
    super(message);
    this.name = "APIError";
  }
}

// Types
type RequestMethod = "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
type RequestOptions = RequestInit & { params?: Record<string, string> };

// API Client configuration
const API_BASE = import.meta.env.VITE_API_URL || "";
const DEFAULT_STALE_TIME = 5 * 60 * 1000; // 5 minutes

// Get CSRF token from cookies
function getCsrfToken(): string | null {
  const name = "csrftoken";
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop()?.split(";").shift() || null;
  }
  return null;
}

// Utility to build URL with query parameters
function buildUrl(path: string, params?: Record<string, string>): string {
  const url = `${API_BASE}/api${path.startsWith("/") ? path : `/${path}`}`;
  if (!params) return url;

  const searchParams = new URLSearchParams(
    Object.fromEntries(
      Object.entries(params).filter(([, value]) => value != null),
    ),
  );
  const queryString = searchParams.toString();
  return queryString ? `${url}?${queryString}` : url;
}

// Query key generation
export const generateQueryKey = (path: string): QueryKey =>
  path.split("/").filter(Boolean);

// Main API client
export const api = {
  async request<T>(
    method: RequestMethod,
    path: string,
    { params, headers = {}, ...config }: RequestOptions = {},
  ): Promise<T> {
    const requestHeaders = new Headers({
      "Content-Type": "application/json",
      ...headers,
    });

    // Add CSRF token for non-GET requests
    if (method !== "GET") {
      const csrfToken = getCsrfToken();
      if (csrfToken) {
        requestHeaders.set("X-CSRFToken", csrfToken);
      }
    }

    const response = await fetch(buildUrl(path, params), {
      method,
      credentials: "include",
      headers: requestHeaders,
      ...config,
    });

    if (!response.ok) {
      const errorData: APIErrorData | null = await response
        .json()
        .catch(() => null);
      throw new APIError(
        errorData?.message || `API Error: ${response.statusText}`,
        response.status,
        errorData,
      );
    }

    return response.status === 204 ? ({} as T) : response.json();
  },

  get: <T>(path: string, options?: RequestOptions) =>
    api.request<T>("GET", path, options),
  post: <T>(path: string, data?: unknown, options: RequestOptions = {}) =>
    api.request<T>("POST", path, { ...options, body: JSON.stringify(data) }),
  put: <T>(path: string, data?: unknown, options: RequestOptions = {}) =>
    api.request<T>("PUT", path, { ...options, body: JSON.stringify(data) }),
  patch: <T>(path: string, data?: unknown, options: RequestOptions = {}) =>
    api.request<T>("PATCH", path, { ...options, body: JSON.stringify(data) }),
  delete: <T>(path: string, options?: RequestOptions) =>
    api.request<T>("DELETE", path, options),

  useQuery<TData>(
    path: string,
    options?: Omit<UseQueryOptions<TData, APIError>, "queryKey" | "queryFn">,
  ) {
    return useQuery<TData, APIError>({
      queryKey: generateQueryKey(path),
      queryFn: () => api.get<TData>(path),
      staleTime: DEFAULT_STALE_TIME,
      ...options,
    });
  },

  useQueryWithTransform<TData, TRData>(
    path: string,
    transform: (data: TData) => TRData,
    options?: Omit<UseQueryOptions<TRData, APIError>, "queryKey" | "queryFn">,
  ) {
    return useQuery<TRData, APIError>({
      queryKey: generateQueryKey(path),
      queryFn: () => api.get<TData>(path).then(transform),
      staleTime: DEFAULT_STALE_TIME,
      ...options,
    });
  },
};
